/**
 * Pagina Lista Thread di Notifica SRP — CRA Art.14(2) (E10.6 — RF-6-3, rework multi-step).
 *
 * Route: /enisa-reports (vista in `?vista=aperti|precedenti|tutti`, default aperti)
 *
 * Tre viste:
 * - «Aperti» (default): thread non ancora chiusi (threadStatus 'open' o 'pending_investigation');
 * - «Precedenti»: report chiusi ('resolved'/'not_needed'), dal più recente;
 * - «Tutti».
 * Click su una riga naviga al dettaglio (/enisa-reports/:id), dove si gestiscono note,
 * categoria, step e risoluzione — anche dei report precedenti, che restano modificabili.
 *
 * Funzionalità:
 * - Tabella dei thread (Prodotto/release, Categoria ENISA, CVE, Creato, SLA, Avanzamento, Stato)
 * - Ricerca per prodotto, release, CVE o riferimento
 * - Countdown SLA live per riga: i 3 step CRA Art.14(2) (24h/72h/14gg — relazione finale a 30gg
 *   dalla notifica 72h per i Severe Incident) ancorati al timer SLA del report (fallback:
 *   detection del payload, poi creazione). Lo step attivo (primo non ancora notificato) è in
 *   grassetto, i successivi in grigio scuro; uno step già notificato (timer fermo) mostra la
 *   spunta verde, con il ritardo nel tooltip se registrato dopo la scadenza.
 * - Stati loading/empty/error
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import { useSrpReports, useSlaTimers } from './hooks.js';
import { slaAnchorMs, timerForReport } from './slaAnchor.js';
import { formatRemainingMs } from './SlaCountdown.js';
import type { SrpReport, SlaTimer, NotificationStep } from './types.js';
import {
  SLA_STEP_ORDER,
  stepDeadlineMs,
  stepRecordedAtMs,
  stepShortLabel,
} from './slaDeadlines.js';
import { categoriaDiReport, REPORT_CATEGORY_LABEL, type ReportCategory } from './category.js';
import {
  statoEffettivo,
  isThreadChiuso,
  etichettaThread,
  tooltipThread,
  classeBadgeThread,
  dataBreve,
} from './threadStatus.js';
import {
  enisaReportPath,
  enisaReportsListPath,
  REPORT_VIEWS,
  REPORT_VIEW_PARAM,
  type ReportView,
} from './routes.js';
import { LinkTicketOrigine, useTicketDiOrigine } from './originTickets.js';
import styles from './Srp.module.css';

// ---------------------------------------------------------------------------
// Helper stato thread
// ---------------------------------------------------------------------------

function classeBadgeCategoria(categoria: ReportCategory): string {
  const b = styles.badge ?? '';
  return categoria === 'severe_incident'
    ? `${b} ${styles.badgeCategoriaIncident ?? ''}`
    : `${b} ${styles.badgeCategoriaAev ?? ''}`;
}

// ---------------------------------------------------------------------------
// Helper SLA — countdown live per i 3 step CRA Art.14(2)
// ---------------------------------------------------------------------------

/** Hook che restituisce `Date.now()` aggiornato a intervalli regolari (countdown live). */
function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
    }, intervalMs);
    return () => {
      clearInterval(id);
    };
  }, [intervalMs]);
  return now;
}

/**
 * Countdown SLA live per riga: una riga per ciascuno dei 3 step CRA Art.14(2).
 * Lo step attivo (primo non ancora notificato) è in grassetto; i successivi in
 * grigio scuro; uno step già notificato mostra la spunta verde di fianco alla soglia.
 * Il counter di ogni step punta alla scadenza assoluta `detection + soglia`, quindi
 * il counter del 72h vale «72 − ore già trascorse» (es. 72−24−extra dopo il 24h).
 */
function SlaThreadCountdown({
  report,
  timer,
  now,
}: {
  report: SrpReport;
  timer?: SlaTimer;
  now: number;
}) {
  const ancora = slaAnchorMs(report, timer);
  if (ancora === null) {
    return (
      <span data-testid={`sla-${report.id}`} style={{ color: '#adb5bd' }}>
        —
      </span>
    );
  }

  const notificati = new Set((report.payload.notifications ?? []).map((n) => n.step));
  const stepAttivo = SLA_STEP_ORDER.find((step) => !notificati.has(step));

  return (
    <div className={styles.slaSteps} data-testid={`sla-${report.id}`}>
      {SLA_STEP_ORDER.map((step) => {
        const breve = stepShortLabel(report, step);
        const inviato = notificati.has(step);
        const attivo = step === stepAttivo;
        const deadlineMs = stepDeadlineMs(report, ancora, step);
        const remainingMs = deadlineMs - now;
        const scaduto = remainingMs <= 0;
        // Step registrato: timer fermo alla registrazione (in tempo o in ritardo).
        const registratoMs = stepRecordedAtMs(report, step);
        const inRitardo = registratoMs !== null && registratoMs >= deadlineMs;

        let classe = styles.slaStepFuture ?? '';
        let stato: 'done' | 'active' | 'future' = 'future';
        if (inviato) {
          classe = styles.slaStepDone ?? '';
          stato = 'done';
        } else if (attivo) {
          classe = `${styles.slaStepActive ?? ''} ${scaduto ? (styles.slaStepBreached ?? '') : ''}`;
          stato = 'active';
        }

        return (
          <span
            key={step}
            className={classe}
            data-testid={`sla-${report.id}-${step}`}
            data-state={stato}
          >
            {inviato ? (
              <>
                {breve}{' '}
                <span
                  data-testid={`sla-check-${report.id}-${step}`}
                  aria-label={inRitardo ? 'notificato in ritardo' : 'notificato'}
                  title={
                    inRitardo
                      ? `timer fermato: registrato in ritardo di ${formatRemainingMs((registratoMs ?? 0) - deadlineMs || 1)}`
                      : 'timer fermato: registrato in tempo'
                  }
                  data-late={inRitardo ? 'true' : 'false'}
                >
                  ✓
                </span>
              </>
            ) : (
              <>
                {breve}: {formatRemainingMs(remainingMs)}
              </>
            )}
          </span>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper Avanzamento — 3 step indicator
// ---------------------------------------------------------------------------

const STEP_LABELS: Record<NotificationStep, string> = {
  early_warning: 'Early warning',
  vulnerability: 'Notifica 72h',
  final: 'Relazione finale',
};

function Avanzamento({ report }: { report: SrpReport }) {
  const notificati = new Set((report.payload.notifications ?? []).map((n) => n.step));
  return (
    <div className={styles.progressSteps} data-testid={`avanzamento-${report.id}`}>
      {SLA_STEP_ORDER.map((step) => {
        const fatto = notificati.has(step);
        return (
          <span
            key={step}
            className={`${styles.progressStep ?? ''} ${fatto ? (styles.progressStepDone ?? '') : ''}`}
            title={STEP_LABELS[step]}
            data-testid={`avanzamento-${report.id}-${step}`}
          >
            {fatto ? '●' : '○'}
          </span>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Riga thread
// ---------------------------------------------------------------------------

interface RigaThreadProps {
  report: SrpReport;
  /** Timer SLA del report (ancora del clock CRA); assente → fallback su detectedAt/createdAt. */
  timer?: SlaTimer;
  now: number;
  /** Ticket la cui promozione ha originato il report. */
  ticketOrigine: number[];
}

function RigaThread({ report, timer, now, ticketOrigine }: RigaThreadProps) {
  const navigate = useNavigate();

  const prodotto = report.payload.affectedProduct;
  const cves = report.payload.cves;
  const conteggioCve = cves?.length ?? 1;
  const stato = statoEffettivo(report);
  const categoria = categoriaDiReport(report.payload);

  return (
    <tr
      data-testid={`riga-srp-${report.id}`}
      onClick={() => {
        navigate(enisaReportPath(report.id));
      }}
      style={{ cursor: 'pointer' }}
    >
      <td>
        {prodotto ? (
          <span>
            {prodotto.name} <span style={{ color: '#6c757d' }}>v{prodotto.releaseVersion}</span>
          </span>
        ) : (
          <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{report.subjectRef}</span>
        )}
      </td>
      <td data-testid={`categoria-${report.id}`}>
        <span className={classeBadgeCategoria(categoria)} data-category={categoria}>
          {REPORT_CATEGORY_LABEL[categoria]}
        </span>
      </td>
      <td>{conteggioCve}</td>
      <td data-testid={`creato-${report.id}`}>{dataBreve(report.createdAt)}</td>
      <td>
        {/* Thread chiuso: le scadenze non corrono più → niente countdown. */}
        {isThreadChiuso(report) ? (
          <span data-testid={`sla-${report.id}`} className={styles.muted ?? ''}>
            —
          </span>
        ) : (
          <SlaThreadCountdown report={report} timer={timer} now={now} />
        )}
      </td>
      <td>
        <Avanzamento report={report} />
      </td>
      <td>
        <span
          className={classeBadgeThread(stato)}
          title={tooltipThread(stato)}
          data-testid={`stato-${report.id}`}
        >
          {etichettaThread(stato)}
        </span>
      </td>
      <td>
        <LinkTicketOrigine reportId={report.id} ticketIds={ticketOrigine} />
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Pagina principale
// ---------------------------------------------------------------------------

const VIEW_LABEL: Record<ReportView, string> = {
  aperti: 'Aperti',
  precedenti: 'Precedenti',
  tutti: 'Tutti',
};

const EMPTY_MSG: Record<ReportView, string> = {
  aperti: 'Nessun thread di notifica SRP aperto.',
  precedenti: 'Nessun report precedente (chiuso).',
  tutti: 'Nessun report ENISA.',
};

function inVista(report: SrpReport, view: ReportView): boolean {
  if (view === 'tutti') return true;
  return view === 'precedenti' ? isThreadChiuso(report) : !isThreadChiuso(report);
}

/** Testo su cui cerca il filtro: prodotto, release, riferimento, CVE. */
function testoRicerca(report: SrpReport): string {
  const p = report.payload;
  return [
    p.affectedProduct?.name,
    p.affectedProduct?.releaseVersion,
    report.subjectRef,
    p.vulnerability?.cveId,
    ...(p.cves ?? []).map((c) => c.cveId),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/** Più recente prima (createdAt; i report senza data in coda, per id decrescente). */
function piuRecentePrima(a: SrpReport, b: SrpReport): number {
  const ta = a.createdAt ? new Date(a.createdAt).getTime() : -Infinity;
  const tb = b.createdAt ? new Date(b.createdAt).getTime() : -Infinity;
  if (ta !== tb) return tb - ta;
  return b.id - a.id;
}

function parseVista(raw: string | null): ReportView {
  return REPORT_VIEWS.includes(raw as ReportView) ? (raw as ReportView) : 'aperti';
}

export function SrpListPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { data: reports, isLoading, isError, error } = useSrpReports();
  // Timer SLA: ancora del clock CRA per riga (best-effort: se la query fallisce si ricade
  // su detectedAt/createdAt del report).
  const { data: timers } = useSlaTimers();
  const ticketDiOrigine = useTicketDiOrigine();
  const now = useNow(1000);
  const [searchParams] = useSearchParams();
  const vista = parseVista(searchParams.get(REPORT_VIEW_PARAM));
  const [ricerca, setRicerca] = useState('');

  const tutti = reports ?? [];
  const conteggio: Record<ReportView, number> = {
    aperti: tutti.filter((r) => inVista(r, 'aperti')).length,
    precedenti: tutti.filter((r) => inVista(r, 'precedenti')).length,
    tutti: tutti.length,
  };
  const q = ricerca.trim().toLowerCase();
  const visibili = tutti
    .filter((r) => inVista(r, vista))
    .filter((r) => q === '' || testoRicerca(r).includes(q));
  // I thread aperti restano nell'ordine del servizio; precedenti/tutti dal più recente.
  if (vista !== 'aperti') visibili.sort(piuRecentePrima);

  return (
    <div data-testid="srp-list-page">
      {/* Intestazione (nascosta quando la lista è incorporata nella pagina Report) */}
      {!embedded && (
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Thread di notifica SRP</h1>
          <span className={styles.pageSubtitle}>CRA Art.14(2) — E10.6 / RF-6-3</span>
        </div>
      )}

      {/* Viste: aperti / precedenti / tutti + ricerca */}
      <nav className={styles.tabs} role="tablist" aria-label="Viste report ENISA">
        {REPORT_VIEWS.map((v) => (
          <Link
            key={v}
            to={enisaReportsListPath(v)}
            role="tab"
            aria-selected={vista === v}
            className={`${styles.tab ?? ''} ${vista === v ? (styles.tabActive ?? '') : ''}`}
            data-testid={`vista-${v}`}
          >
            {VIEW_LABEL[v]}
            {reports && <span className={styles.tabCount}>{conteggio[v]}</span>}
          </Link>
        ))}
        <input
          type="search"
          className={styles.searchInput}
          placeholder="Cerca prodotto, release o CVE"
          aria-label="Cerca report"
          value={ricerca}
          onChange={(e) => {
            setRicerca(e.target.value);
          }}
          data-testid="srp-ricerca"
        />
      </nav>

      {/* Stati caricamento / errore */}
      {isLoading && (
        <p className={styles.loadingMsg} data-testid="srp-loading">
          Caricamento report SRP...
        </p>
      )}

      {isError && (
        <div className={styles.errorMsg} data-testid="srp-error">
          {error instanceof ApiError ? error.message : (error?.message ?? 'Errore sconosciuto')}
        </div>
      )}

      {/* Lista vuota */}
      {!isLoading && !isError && visibili.length === 0 && (
        <div className={styles.emptyState} data-testid="srp-vuoto">
          {q !== '' ? 'Nessun report corrisponde alla ricerca.' : EMPTY_MSG[vista]}
        </div>
      )}

      {/* Tabella thread */}
      {!isLoading && !isError && visibili.length > 0 && (
        <table className={styles.table} data-testid="tabella-srp" data-view={vista}>
          <thead>
            <tr>
              <th>Prodotto / release</th>
              <th>Categoria</th>
              <th>CVE</th>
              <th>Creato</th>
              <th>SLA</th>
              <th>Avanzamento</th>
              <th>Stato</th>
              <th>Ticket</th>
            </tr>
          </thead>
          <tbody>
            {visibili.map((report) => (
              <RigaThread
                key={report.id}
                report={report}
                timer={timerForReport(report, timers)}
                now={now}
                ticketOrigine={ticketDiOrigine(report)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
